import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import useCase from "@hooks/case/useCase";
import { withConditionRestored } from "@hooks/case/useRemoveMedication";
import useDataLayer from "@hooks/useDataLayer";
import {
  medicationRestored,
  selectRemovedMedications,
} from "@store/removedMedications/removedMedicationsSlice";
import { GA4Events } from "@types-runway/analytics";
import { Case } from "@types-runway/case";
import { RemovedMedication } from "@types-runway/removedMedication";
import { RootEntity, RootState } from "@types-runway/state";

const useRestoreMedication = () => {
  const dispatch = useDispatch();
  const newCase = useSelector<RootState, RootEntity<Case>>((state) => state.case.newCase);
  const removedMedications = useSelector<RootState, RemovedMedication[]>((state) =>
    selectRemovedMedications(state, state.case.newCase.data.id)
  );
  const pendingRestoreRef = useRef<string | null>(null);
  const { updateCase } = useCase();
  const { reportAddToCart } = useDataLayer(GA4Events.ADD_TO_CART);

  // The entry leaves the removed list only once the save has succeeded.
  useEffect(() => {
    const pendingName = pendingRestoreRef.current;
    if (!pendingName || newCase.isPending) return;

    pendingRestoreRef.current = null;
    const isRestored = (newCase.data.conditions ?? []).some(
      (condition) => condition.product.name === pendingName
    );
    if (isRestored && newCase.data.id) {
      dispatch(medicationRestored({ caseId: newCase.data.id, productName: pendingName }));
    }
  }, [newCase.isPending, newCase.data.conditions]);

  const restoreMedication = useCallback(
    (productName: string) => {
      const conditions = newCase.data.conditions ?? [];
      const removed = removedMedications.find(
        (entry) => entry.condition.product.name === productName
      );
      const caseId = newCase.data.id;
      const isAlreadyInOrder = conditions.some(
        (condition) => condition.product.name === productName
      );

      if (newCase.isPending || !removed || !caseId || isAlreadyInOrder) return;

      pendingRestoreRef.current = productName;
      updateCase(caseId, Boolean(newCase.data.UserId), {
        conditions: withConditionRestored(conditions, removed),
      });
      reportAddToCart(removed.condition);
    },
    [newCase, removedMedications, updateCase, reportAddToCart]
  );

  return { restoreMedication, removedMedications };
};

export default useRestoreMedication;
